import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from '@/components/ui/toaster'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { SessionExpiredDialog } from '@/components/SessionExpiredDialog'
import { useSessionExpired } from '@/hooks/useSessionExpired'
import { Layout } from '@/components/Layout'
import { Dashboard } from '@/pages/Dashboard'
import { Sequences } from '@/pages/Sequences'
import { SequenceDetail } from '@/pages/SequenceDetail'
import { Contacts } from '@/pages/Contacts'
import { ContactDetail } from '@/pages/ContactDetail'
import { Segments } from '@/pages/Segments'
import { SegmentDetail } from '@/pages/SegmentDetail'
import { Enrollments } from '@/pages/Enrollments'
import { Settings } from '@/pages/Settings'
import { Docs } from '@/pages/Docs'
import { Feedback } from '@/pages/Feedback'
import { FeedbackDetail } from '@/pages/FeedbackDetail'
import { Signatures } from '@/pages/Signatures'
import { Login } from '@/pages/Login'
import { Register } from '@/pages/Register'
import { AcceptInvite } from '@/pages/AcceptInvite'
import { ForgotPassword } from '@/pages/ForgotPassword'
import { ResetPassword } from '@/pages/ResetPassword'
import { PrivacyPolicy } from '@/pages/PrivacyPolicy'
import { TermsOfService } from '@/pages/TermsOfService'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = !!localStorage.getItem('access_token')
  if (!isAuthenticated) return <Navigate to="/login" replace />
  return <>{children}</>
}

function GuestRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = !!localStorage.getItem('access_token')
  if (isAuthenticated) return <Navigate to="/" replace />
  return <>{children}</>
}

function App() {
  const { i18n } = useTranslation()
  useEffect(() => {
    document.documentElement.lang = i18n.language
  }, [i18n.language])

  const { isOpen, handleReLoginSuccess, handleExit } = useSessionExpired()

  return (
    <ErrorBoundary>
      <Routes>
        <Route path="/login" element={<GuestRoute><Login /></GuestRoute>} />
        <Route path="/register" element={<GuestRoute><Register /></GuestRoute>} />
        <Route path="invite/:token" element={<AcceptInvite />} />
        <Route path="/forgot-password" element={<GuestRoute><ForgotPassword /></GuestRoute>} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
          <Route index element={<Dashboard />} />
          <Route path="sequences" element={<Sequences />} />
          <Route path="sequences/:id" element={<SequenceDetail />} />
          <Route path="contacts" element={<Contacts />} />
          <Route path="contacts/:id" element={<ContactDetail />} />
          <Route path="segments" element={<Segments />} />
          <Route path="segments/:id" element={<SegmentDetail />} />
          <Route path="enrollments" element={<Enrollments />} />
          <Route path="signatures" element={<Signatures />} />
          <Route path="settings" element={<Settings />} />
          <Route path="docs" element={<Docs />} />
          <Route path="feedback" element={<Feedback />} />
          <Route path="feedback/:id" element={<FeedbackDetail />} />
        </Route>
      </Routes>
      <Toaster />
      <SessionExpiredDialog
        isOpen={isOpen}
        onReLoginSuccess={handleReLoginSuccess}
        onExit={handleExit}
      />
    </ErrorBoundary>
  )
}

export default App
